import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Sparkles, Loader2, Crown, Heart, Calendar as CalendarIcon, Check, X } from "lucide-react";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { IMAGES } from "@/lib/images";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import LuvmeIcon from "@/components/LuvmeIcon";

function ageFrom(dobStr) {
  const dob = new Date(dobStr);
  if (isNaN(dob)) return null;
  const now = new Date();
  let age = now.getFullYear() - dob.getFullYear();
  const m = now.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) age--;
  return age;
}

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [role, setRole] = useState("creator");
  const [form, setForm] = useState({ display_name: "", email: "", password: "", date_of_birth: "" });
  const [terms, setTerms] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [nick, setNick] = useState(null); // null | { available, reason }
  const [nickChecking, setNickChecking] = useState(false);

  useEffect(() => {
    const value = form.display_name.trim();
    if (!value) { setNick(null); return; }
    setNickChecking(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/auth/check-nickname", { params: { nickname: value } });
        setNick(data);
      } catch {
        setNick(null);
      } finally {
        setNickChecking(false);
      }
    }, 450);
    return () => clearTimeout(t);
  }, [form.display_name]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const age = ageFrom(form.date_of_birth);
    if (age === null) { setError("Inserisci una data di nascita valida."); return; }
    if (age < 18) { setError("Devi avere almeno 18 anni per registrarti su Luvme."); return; }
    if (!/^[a-zA-Z0-9_.]{3,20}$/.test(form.display_name.trim())) {
      setError("Il nickname deve avere 3-20 caratteri (lettere, numeri, . o _) senza spazi. Usa uno pseudonimo, non il tuo nome reale.");
      return;
    }
    if (form.password.length < 8 || !/[a-z]/.test(form.password) || !/[A-Z]/.test(form.password) || !/\d/.test(form.password)) {
      setError("La password deve avere almeno 8 caratteri, con maiuscola, minuscola e un numero.");
      return;
    }
    if (!terms) { setError("Devi confermare di avere almeno 18 anni e accettare i Termini di Servizio."); return; }
    setLoading(true);
    try {
      const user = await register({ ...form, role, terms_accepted: terms });
      navigate(user.role === "creator" ? "/onboarding" : "/feed");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const RoleCard = ({ value, icon: Icon, title, desc }) => (
    <button
      type="button"
      onClick={() => setRole(value)}
      data-testid={`role-${value}`}
      className={`flex-1 text-left p-4 rounded-2xl border transition-all duration-300 ${
        role === value ? "border-[#E91E8C] bg-[#E91E8C]/10" : "border-white/10 bg-[#1A1A1A] hover:border-white/25"
      }`}
    >
      <Icon className={`w-6 h-6 mb-2 ${role === value ? "text-[#E91E8C]" : "text-white/60"}`} />
      <p className="font-semibold text-sm">{title}</p>
      <p className="text-xs text-white/50 mt-0.5">{desc}</p>
    </button>
  );

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#0D0D0D]">
      <div className="flex items-center justify-center p-6 sm:p-12 order-2 lg:order-1">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2 mb-10">
            <LuvmeIcon className="w-6 h-6 sm:w-7 sm:h-7" />
            <span className="text-2xl font-black font-heading">Luvme</span>
          </Link>
          <h1 className="text-3xl font-black font-heading mb-2">Crea il tuo account</h1>
          <p className="text-white/60 mb-6">Inizia a guadagnare di più oggi.</p>

          <div className="flex gap-3 mb-6">
            <RoleCard value="creator" icon={Crown} title="Sono un Creator" desc="Pubblica e guadagna" />
            <RoleCard value="fan" icon={Heart} title="Sono un Fan" desc="Supporta i creator" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-white/80">Nickname (pseudonimo)</Label>
              <Input
                required value={form.display_name} onChange={set("display_name")} data-testid="reg-name"
                maxLength={20}
                className="mt-2 bg-[#1A1A1A] border-white/10 h-12 rounded-xl focus:border-[#E91E8C] focus-visible:ring-[#E91E8C]"
                placeholder="es. stella_92"
              />
              <p className="text-xs text-white/40 mt-1.5" data-testid="reg-name-hint">
                Per la tua privacy usa uno pseudonimo, mai il tuo nome reale. 3-20 caratteri, niente spazi.
              </p>
              {form.display_name.trim() && (
                <div className="mt-1.5 flex items-center gap-1.5 text-xs" data-testid="reg-nick-status">
                  {nickChecking ? (
                    <span className="text-white/40 flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> Verifica…</span>
                  ) : nick ? (
                    nick.available ? (
                      <span className="text-green-400 flex items-center gap-1.5"><Check className="w-3.5 h-3.5" /> {nick.reason}</span>
                    ) : (
                      <span className="text-red-400 flex items-center gap-1.5"><X className="w-3.5 h-3.5" /> {nick.reason}</span>
                    )
                  ) : null}
                </div>
              )}
            </div>
            <div>
              <Label className="text-white/80">Email</Label>
              <Input
                type="email" required value={form.email} onChange={set("email")} data-testid="reg-email"
                className="mt-2 bg-[#1A1A1A] border-white/10 h-12 rounded-xl focus:border-[#E91E8C] focus-visible:ring-[#E91E8C]"
                placeholder="tu@esempio.com"
              />
            </div>
            <div>
              <Label className="text-white/80">Password</Label>
              <Input
                type="password" required minLength={8} value={form.password} onChange={set("password")} data-testid="reg-password"
                className="mt-2 bg-[#1A1A1A] border-white/10 h-12 rounded-xl focus:border-[#E91E8C] focus-visible:ring-[#E91E8C]"
                placeholder="Min 8 caratteri, maiuscola, minuscola e numero"
              />
            </div>
            <div>
              <Label className="text-white/80">Data di nascita</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button" data-testid="reg-dob"
                    className="mt-2 w-full flex items-center justify-between bg-[#1A1A1A] border border-white/10 h-12 rounded-xl px-3.5 text-left hover:border-[#E91E8C]/50 transition-colors focus:outline-none focus:border-[#E91E8C]"
                  >
                    <span className={form.date_of_birth ? "text-white" : "text-white/40"}>
                      {form.date_of_birth ? format(new Date(form.date_of_birth), "dd/MM/yyyy") : "gg/mm/aaaa"}
                    </span>
                    <CalendarIcon className="w-4 h-4 text-white/50" />
                  </button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-auto p-0 bg-[#1A1A1A] border-white/10 text-white">
                  <Calendar
                    mode="single"
                    selected={form.date_of_birth ? new Date(form.date_of_birth) : undefined}
                    onSelect={(d) => d && setForm({ ...form, date_of_birth: format(d, "yyyy-MM-dd") })}
                    captionLayout="dropdown-buttons"
                    fromYear={1940}
                    toYear={new Date().getFullYear()}
                    defaultMonth={new Date(new Date().getFullYear() - 18, 0)}
                    disabled={(d) => d > new Date()}
                    locale={it}
                    initialFocus
                    classNames={{
                      caption_dropdowns: "flex gap-1.5",
                      dropdown: "bg-[#0D0D0D] text-white rounded-md px-1.5 py-1 text-sm border border-white/10 focus:outline-none",
                      caption_label: "hidden",
                    }}
                  />
                </PopoverContent>
              </Popover>
              <p className="text-xs text-white/40 mt-1.5">Devi avere almeno 18 anni. Verifica età obbligatoria.</p>
            </div>
            <div className="flex items-start gap-3 rounded-xl border border-white/10 bg-[#1A1A1A] p-3">
              <Checkbox
                id="reg-terms" checked={terms} onCheckedChange={(v) => setTerms(!!v)} data-testid="reg-terms"
                className="mt-0.5 border-white/30 data-[state=checked]:bg-[#E91E8C] data-[state=checked]:border-[#E91E8C]"
              />
              <Label htmlFor="reg-terms" className="text-xs text-white/70 leading-relaxed cursor-pointer">
                Confermo di avere almeno 18 anni e accetto i <span className="text-[#E91E8C]">Termini di Servizio</span> e la Privacy Policy di Luvme.
              </Label>
            </div>
            {error && <p className="text-sm text-red-400" data-testid="reg-error">{error}</p>}
            <Button
              type="submit" disabled={loading || !terms} data-testid="reg-submit"
              className="w-full h-12 rounded-xl bg-[#E91E8C] hover:bg-[#D8157B] text-white font-semibold hover:shadow-[0_0_18px_rgba(233,30,140,0.5)] transition-all disabled:opacity-50"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Registrati"}
            </Button>
          </form>

          <p className="text-center text-white/60 mt-6 text-sm">
            Hai già un account?{" "}
            <Link to="/login" className="text-[#E91E8C] font-semibold hover:underline" data-testid="go-login">Accedi</Link>
          </p>
        </div>
      </div>

      <div className="hidden lg:block relative order-1 lg:order-2">
        <img src={IMAGES.cover1} alt="Luvme" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0D0D0D] via-[#0D0D0D]/40 to-transparent" />
        <div className="absolute bottom-12 left-8 right-8" data-testid="register-claim">
          {role === "creator" ? (
            <>
              <h2 className="text-4xl font-black font-heading leading-tight">Guadagna di più,<br /><span className="text-[#E91E8C]">tieni di più.</span></h2>
              <p className="text-white/70 mt-3">Solo 15% di commissione.</p>
            </>
          ) : (
            <>
              <h2 className="text-4xl font-black font-heading leading-tight">Sostieni i creator<br /><span className="text-[#E91E8C]">che ami.</span></h2>
              <p className="text-white/70 mt-3">Contenuti esclusivi, accesso diretto.</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
